import { Menu as BaseMenu } from '@base-ui/react/menu';
import type { ComponentProps } from 'react';
import { CheckIcon, ChevronRightIcon } from '../lib/icons.tsx';
import { LAYER } from '../lib/layer.ts';
import { cx, styled } from '../lib/styled.tsx';
import {
  groupLabelSurface,
  itemSurface,
  popupSeparator,
  popupSurface,
  popupTransition,
} from '../lib/surfaces.ts';

const popup =
  `${popupSurface} ${popupTransition} ` +
  'min-w-60 max-h-[var(--available-height)] overflow-y-auto p-2';

/**
 * A tick by default, so the common checkbox/radio row needs no extra markup.
 *
 * Kept mounted and merely hidden when unchecked, so every row in a menu shares
 * one column for its tick: without it, unchecked labels slide left.
 */
function CheckboxItemIndicator({
  children,
  className,
  ...props
}: ComponentProps<typeof BaseMenu.CheckboxItemIndicator>) {
  return (
    <BaseMenu.CheckboxItemIndicator
      keepMounted
      className={cx(
        'flex size-4 shrink-0 items-center justify-center text-brand-600 data-unchecked:invisible',
        className,
      )}
      {...props}
    >
      {children ?? <CheckIcon className="size-3.5" />}
    </BaseMenu.CheckboxItemIndicator>
  );
}

function RadioItemIndicator({
  children,
  className,
  ...props
}: ComponentProps<typeof BaseMenu.RadioItemIndicator>) {
  return (
    <BaseMenu.RadioItemIndicator
      keepMounted
      className={cx(
        'flex size-4 shrink-0 items-center justify-center text-brand-600 data-unchecked:invisible',
        className,
      )}
      {...props}
    >
      {children ?? <span className="size-1.5 rounded-full bg-current" />}
    </BaseMenu.RadioItemIndicator>
  );
}

function SubmenuTrigger({
  children,
  className,
  ...props
}: ComponentProps<typeof BaseMenu.SubmenuTrigger>) {
  return (
    <BaseMenu.SubmenuTrigger
      className={cx(itemSurface, 'justify-between data-popup-open:bg-surface-muted', className)}
      {...props}
    >
      {children}
      <ChevronRightIcon className="size-3.5 shrink-0 text-ink-muted" />
    </BaseMenu.SubmenuTrigger>
  );
}

/**
 * A list of actions raised by a button.
 *
 * Kept as parts: menus differ in what they hold — plain items, links, checkbox
 * and radio rows, submenus — and a prop-driven shape would need a prop per
 * variation. Render your own {@link Button} through `Trigger`'s `render` prop.
 */
export const Menu = {
  /** State only — renders no element of its own. */
  Root: BaseMenu.Root,
  Trigger: styled(BaseMenu.Trigger, 'inline-flex items-center gap-2 outline-none'),
  Portal: BaseMenu.Portal,
  Backdrop: styled(BaseMenu.Backdrop, 'fixed inset-0'),
  Positioner: styled(BaseMenu.Positioner, `${LAYER.popup} outline-none`, { sideOffset: 6 }),
  Popup: styled(BaseMenu.Popup, popup),
  Arrow: styled(BaseMenu.Arrow, ''),
  Item: styled(BaseMenu.Item, itemSurface),
  LinkItem: styled(BaseMenu.LinkItem, `${itemSurface} no-underline`),
  Separator: styled(BaseMenu.Separator, popupSeparator),
  Group: styled(BaseMenu.Group, ''),
  GroupLabel: styled(BaseMenu.GroupLabel, groupLabelSurface),
  RadioGroup: styled(BaseMenu.RadioGroup, ''),
  RadioItem: styled(BaseMenu.RadioItem, itemSurface),
  RadioItemIndicator,
  CheckboxItem: styled(BaseMenu.CheckboxItem, itemSurface),
  CheckboxItemIndicator,
  SubmenuRoot: BaseMenu.SubmenuRoot,
  SubmenuTrigger,
  /** Wraps the popup's contents so a submenu can animate its size change. */
  Viewport: styled(BaseMenu.Viewport, 'relative'),
};
